import { useEffect, useMemo, useRef, useState } from 'react';

import {
  api,
  type ImportElemzes,
  type ImportEredmeny,
  type ImportSor,
  type JaratNap,
  type ParositasMod,
} from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Járatlap importálása Excelből vagy CSV-ből. (Megbízás 7.2)
 *
 * Az import **nem ír azonnal**. Előbb megmutatja soronként, mit talált a
 * fájlban és mihez párosította — és hogyan. Amit nem tudott egyértelműen
 * beazonosítani, ahhoz legördülőt ad a legvalószínűbb jelöltekkel.
 *
 * A kézzel választott párosításokat a program **megjegyzi**, így ugyanaz a
 * névváltozat legközelebb magától a helyére kerül.
 */

/** Hogyan találta meg a program — ezt őszintén meg kell mutatni. */
const MOD_CIMKE: Record<ParositasMod, string> = {
  pontos: 'pontos név',
  takaritott: 'név egyezik',
  alias: 'korábbi párosítás',
  kezdet: 'név eleje',
  tisztitott: 'jelölések nélkül',
  nincs: 'nincs találat',
};

/** A biztosnak vehető párosítások — ezeket nem kell egyenként átnézni. */
const BIZTOS: ParositasMod[] = ['pontos', 'takaritott', 'alias'];

interface SorDontes {
  /** Ide kerüljön. */
  etterem_id: number | null;
  /** Új étteremként vegyük fel a fájlbeli néven. */
  uj_etterem: boolean;
  /** Kihagyjuk ezt a sort. */
  kihagy: boolean;
  /** Kézzel nyúltál hozzá — ilyenkor jegyezzük meg a párosítást. */
  kezi: boolean;
}

export function JaratlapImport({
  datum,
  onKesz,
  onMegse,
}: {
  datum: string;
  onKesz: (jarat: JaratNap, uzenet: string) => void;
  onMegse: () => void;
}) {
  const [fajl, setFajl] = useState<File | null>(null);
  const [elemzes, setElemzes] = useState<ImportElemzes | null>(null);
  const [dontesek, setDontesek] = useState<Record<number, SorDontes>>({});
  const [mod, setMod] = useState<'hozzaad' | 'felulir'>('hozzaad');
  const [arForras, setArForras] = useState<'torzs' | 'fajl'>('torzs');
  const [csakGondosak, setCsakGondosak] = useState(false);
  const [munkaban, setMunkaban] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);
  const [ettermek, setEttermek] = useState<Array<{ id: number; nev: string }>>([]);
  const fajlMezo = useRef<HTMLInputElement>(null);

  // A teljes étteremlista a legördülőkhöz — egyszer kérjük le.
  useEffect(() => {
    api
      .get<{ ettermek: Array<{ id: number; nev: string }> }>('/api/jarat/import/ettermek')
      .then((v) => setEttermek(v.ettermek))
      .catch(() => setEttermek([]));
  }, []);

  async function beolvas(valasztott: File, munkalap?: string) {
    setMunkaban(true);
    setHiba(null);
    try {
      const ut =
        `/api/jarat/${datum}/import/elemzes` +
        (munkalap ? `?munkalap=${encodeURIComponent(munkalap)}` : '');
      const e = await api.fajl<ImportElemzes>(ut, valasztott);
      setElemzes(e);
      setFajl(valasztott);

      // Alaphelyzet: amit a program megtalált, azt elfogadjuk; a többit
      // kihagyjuk, amíg nem döntesz róla.
      const kezdo: Record<number, SorDontes> = {};
      for (const s of e.sorok) {
        kezdo[s.fajl_sor] = {
          etterem_id: s.etterem_id,
          uj_etterem: false,
          kihagy: s.etterem_id === null,
          kezi: false,
        };
      }
      setDontesek(kezdo);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A fájlt nem sikerült beolvasni.');
      setElemzes(null);
    } finally {
      setMunkaban(false);
    }
  }

  function dontes(fajl_sor: number, valtozas: Partial<SorDontes>) {
    setDontesek((elozo) => ({
      ...elozo,
      [fajl_sor]: { ...elozo[fajl_sor]!, ...valtozas },
    }));
  }

  async function vegrehajt() {
    if (!elemzes) return;
    setMunkaban(true);
    setHiba(null);
    try {
      const sorok = elemzes.sorok.map((s) => {
        const d = dontesek[s.fajl_sor]!;
        return {
          fajl_sor: s.fajl_sor,
          nev: s.nev,
          etterem_id: d.kihagy ? null : d.etterem_id,
          uj_etterem: !d.kihagy && d.uj_etterem,
          kezi: d.kezi,
          mo: s.mo,
          fo: s.fo,
          fplusz: s.fplusz,
          mennyiseg: s.mennyiseg,
          ar: s.ar,
          fizetesi_mod: s.fizetesi_mod,
          megjegyzes: s.megjegyzes,
        };
      });

      const v = await api.post<ImportEredmeny>(`/api/jarat/${datum}/import/vegrehajtas`, {
        sorok,
        mod,
        ar_forras: arForras,
      });

      const reszek = [`${v.felvett} cím felvéve`];
      if (v.torolt) reszek.push(`${v.torolt} korábbi sor törölve`);
      if (v.uj_etterem) reszek.push(`${v.uj_etterem} új étterem`);
      if (v.kihagyott) reszek.push(`${v.kihagyott} sor kihagyva`);
      if (v.megjegyzett_parositas) {
        reszek.push(`${v.megjegyzett_parositas} párosítás megjegyezve`);
      }
      onKesz(v.jarat, reszek.join(' · ') + '.');
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az import nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  /* --- számok a fejlécbe --- */
  const szamok = useMemo(() => {
    if (!elemzes) return null;
    let beker = 0;
    let uj = 0;
    let kimarad = 0;
    let atnezendo = 0;
    for (const s of elemzes.sorok) {
      const d = dontesek[s.fajl_sor];
      if (!d) continue;
      if (d.kihagy) kimarad++;
      else if (d.uj_etterem) {
        uj++;
        beker++;
      } else beker++;
      if (!BIZTOS.includes(s.mod) && !d.kihagy) atnezendo++;
      else if (d.kihagy && s.etterem_id === null) atnezendo++;
    }
    return { beker, uj, kimarad, atnezendo };
  }, [elemzes, dontesek]);

  const lathatoSorok = useMemo(() => {
    if (!elemzes) return [];
    if (!csakGondosak) return elemzes.sorok;
    return elemzes.sorok.filter((s) => !BIZTOS.includes(s.mod) || s.duplikalt);
  }, [elemzes, csakGondosak]);

  /* ------------------------------------------------------------------ */

  return (
    <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
      <h4>Járatlap importálása</h4>

      <div className="btns" style={{ marginTop: 0 }}>
        <label className="btn sm" style={{ cursor: 'pointer', margin: 0 }}>
          Fájl kiválasztása
          <input
            ref={fajlMezo}
            type="file"
            accept=".xlsx,.xlsm,.xltm,.csv,.txt"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void beolvas(f);
              e.target.value = '';
            }}
          />
        </label>
        {fajl && <span className="hint">{fajl.name}</span>}
        {munkaban && <span className="hint">Dolgozom…</span>}
        <span style={{ flex: 1 }}></span>
        <button className="btn sm" onClick={onMegse}>
          × Bezár
        </button>
      </div>

      {!elemzes && !munkaban && (
        <p className="hint" style={{ marginTop: 10 }}>
          Excel (<code>.xlsx</code>, <code>.xlsm</code>, <code>.xltm</code>) vagy{' '}
          <code>.csv</code> fájl. A program a <b>fejléc alapján</b> ismeri fel az oszlopokat,
          és magától megkeresi azt a munkalapot, amin van „Étterem" oszlop — nem baj, ha a
          fájlban máshol vannak az oszlopok, mint tavaly.
        </p>
      )}

      {hiba && <div className="uzenet bad">{hiba}</div>}

      {elemzes && (
        <>
          {/* --- mit talált a fájlban --- */}
          <div className="cards" style={{ marginTop: 12 }}>
            <div className="card">
              <em>A fájlban</em>
              <b>{elemzes.osszes}</b>
              <span>cím</span>
            </div>
            <div className="card">
              <em>Beazonosítva</em>
              <b>{elemzes.parositva}</b>
              <span>magától</span>
            </div>
            <div className={`card${(szamok?.atnezendo ?? 0) > 0 ? ' warn2' : ''}`}>
              <em>Átnézendő</em>
              <b>{szamok?.atnezendo ?? 0}</b>
              <span>sor</span>
            </div>
            <div className="card">
              <em>Bekerül</em>
              <b>{szamok?.beker ?? 0}</b>
              <span>cím{szamok?.uj ? `, ${szamok.uj} új` : ''}</span>
            </div>
            <div className="card">
              <em>Kimarad</em>
              <b>{szamok?.kimarad ?? 0}</b>
              <span>sor</span>
            </div>
          </div>

          <div className="uzenet" style={{ background: 'var(--bluew)', color: 'var(--ink)' }}>
            <b>{elemzes.munkalap}</b> munkalap, a fejléc a {elemzes.fejlec_sor}. sorban.
            Felismert oszlopok:{' '}
            {elemzes.oszlopok.map((o) => `${o.betu}: ${o.fejlec}`).join(' · ')}
            {elemzes.jelolt_lapok.length > 1 && (
              <div className="import-lapok">
                Másik munkalap:{' '}
                {elemzes.jelolt_lapok.map((l) => (
                  <button
                    key={l.nev}
                    className={`btn sm${l.nev === elemzes.munkalap ? ' pri' : ''}`}
                    style={{ marginRight: 6 }}
                    disabled={munkaban || l.nev === elemzes.munkalap}
                    onClick={() => fajl && void beolvas(fajl, l.nev)}
                  >
                    {l.nev} ({l.sorok})
                  </button>
                ))}
              </div>
            )}
          </div>

          {elemzes.figyelmeztetesek.map((f) => (
            <div className="uzenet flag" key={f}>
              {f}
            </div>
          ))}

          {!elemzes.tervezheto && (
            <div className="uzenet bad">
              Ez a nap már lezárult, nem lehet hozzá címet felvenni.
            </div>
          )}

          {/* --- kapcsolók --- */}
          <div className="actbar">
            <span className="lbl">A nap már meglévő {elemzes.jelenlegi_cimek} címével:</span>
            <button
              className={`btn sm${mod === 'hozzaad' ? ' pri' : ''}`}
              onClick={() => setMod('hozzaad')}
            >
              Hozzáadás
            </button>
            <button
              className={`btn sm${mod === 'felulir' ? ' pri' : ''}`}
              onClick={() => setMod('felulir')}
            >
              A nap felülírása
            </button>
            <span style={{ width: 18 }}></span>
            <span className="lbl">Ár:</span>
            <button
              className={`btn sm${arForras === 'torzs' ? ' pri' : ''}`}
              onClick={() => setArForras('torzs')}
            >
              A törzsből
            </button>
            <button
              className={`btn sm${arForras === 'fajl' ? ' pri' : ''}`}
              onClick={() => setArForras('fajl')}
            >
              A fájlból
            </button>
          </div>

          {mod === 'felulir' && elemzes.jelenlegi_cimek > 0 && (
            <div className="uzenet flag">
              A felülírás <b>törli a nap jelenlegi {elemzes.jelenlegi_cimek} sorát</b>, és
              helyükre a fájl kerül.
            </div>
          )}

          {/* --- a párosító táblázat --- */}
          <div className="actbar" style={{ marginTop: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
              <input
                type="checkbox"
                checked={csakGondosak}
                onChange={(e) => setCsakGondosak(e.target.checked)}
              />
              Csak amit át kell nézni
            </label>
            <span className="hint">
              {lathatoSorok.length} sor látszik a {elemzes.osszes}-ből. A biztos párosításokat
              nem kell egyenként jóváhagyni.
            </span>
          </div>

          <div className="matrix" style={{ maxHeight: 460, overflow: 'auto' }}>
            <table className="tt2">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>Sor</th>
                  <th>A fájlban</th>
                  <th style={{ width: 250 }}>Melyik étterem</th>
                  <th style={{ width: 120 }}>Hogyan</th>
                  <th className="n">MO</th>
                  <th className="n">FO</th>
                  <th className="n">F+</th>
                  <th className="n">Menny.</th>
                  <th className="n">Ár</th>
                  <th>Fiz.</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {lathatoSorok.map((s) => (
                  <ImportSorNezet
                    key={s.fajl_sor}
                    s={s}
                    d={dontesek[s.fajl_sor]!}
                    ettermek={ettermek}
                    arForras={arForras}
                    onDontes={(v) => dontes(s.fajl_sor, v)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="btns">
            <button
              className="btn pri"
              disabled={munkaban || !elemzes.tervezheto || (szamok?.beker ?? 0) === 0}
              onClick={() => void vegrehajt()}
            >
              {(szamok?.beker ?? 0) === 0
                ? 'Nincs jóváhagyott sor'
                : `Importálás — ${szamok!.beker} cím${mod === 'felulir' ? ' (felülírás)' : ''}`}
            </button>
            <button className="btn" onClick={onMegse}>
              Mégsem
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ImportSorNezet({
  s,
  d,
  ettermek,
  arForras,
  onDontes,
}: {
  s: ImportSor;
  d: SorDontes;
  ettermek: Array<{ id: number; nev: string }>;
  arForras: 'torzs' | 'fajl';
  onDontes: (v: Partial<SorDontes>) => void;
}) {
  const biztos = BIZTOS.includes(s.mod);
  const gond = !biztos || s.duplikalt;

  // A legördülő elején a legvalószínűbb jelöltek, utána a teljes törzs.
  const jeloltIdk = new Set(s.jeloltek.map((j) => j.etterem_id));
  const tobbi = ettermek.filter((e) => !jeloltIdk.has(e.id));

  // A kihagyott sor halvány; amit át kell nézni, az sárga — a sárga mindig
  // azt jelenti: „nézd meg", sosem azt, hogy hiba.
  const osztalyok = [d.kihagy ? 'gyr' : '', !d.kihagy && gond && !d.kezi ? 'import-gond' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <tr className={osztalyok || undefined}>
      <td className="calc2">{s.fajl_sor}.</td>

      <td className="nm3">
        <b style={{ textDecoration: d.kihagy ? 'line-through' : undefined }}>{s.nev}</b>
        {s.cim && <small>{s.cim}</small>}
        {s.duplikalt && (
          <small style={{ color: 'var(--flag)' }}>ez a név többször is szerepel a fájlban</small>
        )}
      </td>

      <td>
        {d.uj_etterem ? (
          <span style={{ color: 'var(--ok)', fontSize: 12.5 }}>
            új étterem: <b>{s.nev}</b>
          </span>
        ) : (
          <select
            style={{ width: '100%', fontSize: 12.5 }}
            value={d.kihagy ? '' : (d.etterem_id ?? '')}
            onChange={(e) => {
              const ertek = e.target.value;
              if (ertek === 'UJ') onDontes({ uj_etterem: true, kihagy: false, kezi: true });
              else if (ertek === '') onDontes({ etterem_id: null, kihagy: true });
              else onDontes({ etterem_id: Number(ertek), kihagy: false, kezi: true });
            }}
          >
            <option value="">— kihagyjuk ezt a sort —</option>
            {s.jeloltek.length > 0 && (
              <optgroup label="Legvalószínűbb">
                {s.jeloltek.map((j) => (
                  <option key={j.etterem_id} value={j.etterem_id}>
                    {j.nev}
                  </option>
                ))}
              </optgroup>
            )}
            <option value="UJ">+ felvétel új étteremként ezen a néven</option>
            <optgroup label="Minden étterem">
              {tobbi.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nev}
                </option>
              ))}
            </optgroup>
          </select>
        )}
        {d.uj_etterem && (
          <button
            className="btn sm"
            style={{ marginTop: 4 }}
            onClick={() => onDontes({ uj_etterem: false, etterem_id: null, kihagy: true })}
          >
            mégsem
          </button>
        )}
      </td>

      <td>
        <span className={`jelzo ${d.kezi ? 'kezi' : biztos ? 'ok' : 'flag'}`}>
          {d.kezi ? 'kézzel' : s.tobbertelmu ? 'több illik rá' : MOD_CIMKE[s.mod]}
        </span>
      </td>

      <td className="calc2">{s.mo ?? '—'}</td>
      <td className="calc2">{s.fo ?? '—'}</td>
      <td className="calc2">{s.fplusz ?? '—'}</td>
      <td className="calc2">{s.mennyiseg !== null ? ft(s.mennyiseg) : '—'}</td>
      <td className="calc2" title={arForras === 'torzs' ? 'A törzsbeli ár kerül a sorba.' : undefined}>
        {arForras === 'fajl'
          ? s.ar !== null
            ? ft(s.ar)
            : '—'
          : s.torzs_ar !== null
            ? ft(s.torzs_ar)
            : '—'}
      </td>
      <td>
        {s.fizetesi_mod ?? '—'}
        {s.ismeretlen_fizetes && (
          <div style={{ fontSize: 11, color: 'var(--flag)' }}>ismeretlen</div>
        )}
      </td>

      <td className="act">
        {!d.kihagy ? (
          <button className="xb" title="Kihagyás" onClick={() => onDontes({ kihagy: true })}>
            ×
          </button>
        ) : (
          <button
            className="xb"
            title="Mégis bekerüljön"
            onClick={() =>
              onDontes({
                kihagy: false,
                etterem_id: d.etterem_id ?? s.etterem_id ?? s.jeloltek[0]?.etterem_id ?? null,
                kezi: d.etterem_id === null && s.etterem_id === null,
              })
            }
          >
            ↩
          </button>
        )}
      </td>
    </tr>
  );
}
